import { useState, useTransition, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  getAmbassadors,
  getPartnersLeadsCount,
} from "@/modules/ambassadors/api";
import {
  Ambassador,
  GetAmbassadorsParams,
  GetAmbassadorsResponse,
} from "@/modules/ambassadors/types";
import useAuth from "@/modules/common/hooks/auth.hook";
import usePassport from "@/modules/common/hooks/passport";
import { PermissionsEnum, PermissionLevel } from "@/modules/common/types";
import { userHasPermission } from "@/modules/common/utils";

export const useAmbassadors = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [salesAgentDefaultEmail, setSalesAgentDefaultEmail] = useState<
    string | undefined
  >(undefined);
  const [inviterId, setInviterId] = useState<string | undefined>(undefined);

  const navigate = useNavigate();
  const [, startTransition] = useTransition();

  const { user } = useAuth();
  const { permissions: currentUserPermissions = [] } = usePassport();

  const hasSearchPermission = userHasPermission(
    {
      name: PermissionsEnum.ADMIN_VIEW_AMBASSADORS,
      level: PermissionLevel.READ,
    },
    currentUserPermissions
  );

  const params: GetAmbassadorsParams = {
    page: page + 1,
    pageSize: rowsPerPage,
    ...(hasSearchPermission
      ? { ...(inviterId && { inviterId }), ...(searchTerm && { searchTerm }) }
      : { ...(user?.email && { salesAgentDefaultEmail: user.email }) }),
  };

  const {
    data: ambassadorsResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "getAmbassadors",
      page + 1,
      rowsPerPage,
      hasSearchPermission,
      searchTerm,
      inviterId,
      user?.email,
    ],
    queryFn: () => getAmbassadors(params),
    enabled: hasSearchPermission || !!user?.email,
  });

  const defaultResponse: GetAmbassadorsResponse = {
    pages: 0,
    page: 1,
    pageSize: 10,
    count: 0,
    data: [],
  };

  const ambassadorsData: GetAmbassadorsResponse =
    (ambassadorsResponse as any)?.data || defaultResponse;

  const ambassadors: Ambassador[] = useMemo(
    () => ambassadorsData.data || [],
    [ambassadorsData.data]
  );
  const totalCount: number = ambassadorsData.count || 0;

  // Collect ambassador IDs for leads count
  const ambassadorIds = useMemo(
    () =>
      ambassadors
        .filter(
          (ambassador) => ambassador.organization?.brokers?.[0]?.cognitoId
        )
        .map((ambassador) => ambassador.organization!.brokers![0]!.cognitoId),
    [ambassadors]
  );

  // Leads count query
  const { data: leadsCountResponse, isLoading: isLoadingLeadsCount } = useQuery(
    {
      queryKey: ["getPartnersLeadsCount", ambassadorIds],
      queryFn: () => getPartnersLeadsCount(ambassadorIds as string[]),
      enabled: ambassadorIds.length > 0,
    }
  );

  // Merge ambassadors with leads count
  const ambassadorsWithLeadsCount = useMemo(() => {
    if (ambassadorIds.length === 0) {
      return ambassadors.map((ambassador) => ({
        ...ambassador,
        leadsCount: 0,
      }));
    }

    if (!leadsCountResponse)
      return ambassadors.map((ambassador) => ({
        ...ambassador,
        leadsCount: 0,
      }));

    const leadsCountData = (leadsCountResponse as any)?.data || [];

    return ambassadors.map((ambassador) => {
      const cognitoId = ambassador.organization?.brokers?.[0]?.cognitoId;
      if (!cognitoId) {
        return { ...ambassador, leadsCount: 0 };
      }

      const leadCount = leadsCountData.find(
        (lc: { partnerUId: string; count: number }) =>
          lc.partnerUId === cognitoId
      );

      return {
        ...ambassador,
        leadsCount: leadCount ? leadCount.count : 0,
      };
    });
  }, [ambassadors, leadsCountResponse, ambassadorIds.length]);

  // Pagination handlers
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const updateRowsPerPage = (value: number) => {
    setRowsPerPage(value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateRowsPerPage(Number.parseInt(event.target.value, 10));
  };

  const handlePaginationChange = (updater: any) => {
    const newPagination =
      typeof updater === "function"
        ? updater({ pageIndex: page, pageSize: rowsPerPage })
        : updater;

    if (newPagination.pageIndex !== page) {
      handleChangePage(null, newPagination.pageIndex);
    }

    if (newPagination.pageSize !== rowsPerPage) {
      updateRowsPerPage(newPagination.pageSize);
    }
  };

  // Navigation
  const handleViewLeads = (ambassadorId: string) => {
    startTransition(() => {
      navigate(`/?partnerUIds=${ambassadorId}`);
    });
  };

  // Modal handlers
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return {
    // state
    page,
    rowsPerPage,
    isModalOpen,
    ambassadors,
    ambassadorsWithLeadsCount,
    totalCount,
    isLoading,
    isLoadingLeadsCount,
    error,

    // filters (exposed for DefaultFilters)
    searchTerm,
    setSearchTerm: (value: string) => {
      setSearchTerm(value);
      if (page !== 0) setPage(0);
    },
    salesAgentDefaultEmail,
    setSalesAgentDefaultEmail,
    inviterId,
    setInviterId: (value: string | undefined) => {
      setInviterId(value);
      if (page !== 0) setPage(0);
    },

    // handlers
    handleChangePage,
    handleChangeRowsPerPage,
    handlePaginationChange,
    handleViewLeads,
    handleOpenModal,
    handleCloseModal,
  };
};
